import AnchorLink from "@bmi-digital/components/anchor-link";
import ExpandableLinksTextCard from "@bmi-digital/components/expandable-links-text-card";
import MasonryGrid from "@bmi-digital/components/masonry-grid";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import React from "react";
import { graphql } from "gatsby";
import { useSiteContext } from "./Site";
import { toAnchorLinkActionProps } from "./link/utils";
import type { ExpandableLinksTextCardsProps } from "@bmi-digital/components/expandable-links-text-card";
import type { AnchorLinkProps } from "@bmi-digital/components/anchor-link";
import type { ExploreBarSectionData } from "./ExploreBarSection";
import type { PayloadLinkColumnsSection } from "../types/payload";

export type LinkColumnsSectionData = Pick<
  PayloadLinkColumnsSection,
  "title"
> & {
  columns: readonly Pick<ExploreBarSectionData, "title" | "links">[];
};

const LinkColumnsSection = ({ data }: { data: LinkColumnsSectionData }) => {
  const { countryCode, getMicroCopy } = useSiteContext();
  const { title, columns } = data;

  return (
    <Section backgroundColor="pearl" data-testid="link-columns-section">
      {title && <Section.Title>{title}</Section.Title>}
      <MasonryGrid>
        {columns &&
          columns.map(({ title, links }, index) => {
            const linksWithActions: ExpandableLinksTextCardsProps["links"] = (
              links || []
            ).map((link) => {
              return {
                ...toAnchorLinkActionProps(link, countryCode),
                label: link.label
              };
            });

            return (
              <ExpandableLinksTextCard
                key={`${title}-${index}`}
                title={title ? title : undefined}
                openButtonLabel={getMicroCopy(microCopy.GLOBAL_VIEW_MORE)}
                closeButtonLabel={getMicroCopy(microCopy.GLOBAL_VIEW_LESS)}
                links={linksWithActions}
                anchorLinkComponent={(props: AnchorLinkProps) => (
                  <AnchorLink
                    gtm={{
                      id: "cta-click1",
                      action: props.href,
                      label: `${title} - ${props.children}`
                    }}
                    {...props}
                  />
                )}
              />
            );
          })}
      </MasonryGrid>
    </Section>
  );
};

export default LinkColumnsSection;

export const query = graphql`
  fragment LinkColumnsSectionFragment on PayloadLinkColumnsSection {
    title
    columns {
      ... on PayloadExploreBarSection {
        title
        links {
          ... on PayloadInternalLinkBlock {
            ...InternalLinkBlockFragment
          }
          ... on PayloadExternalLinkBlock {
            ...ExternalLinkBlockFragment
          }
        }
      }
    }
  }
`;
